import { Type, Injector, Injectable } from '@angular/core';
import { HttpClient, HttpHandler, HttpResponse, HttpRequest, HttpHeaders, HttpParams } from '@angular/common/http';

import { Observable, Subscriber } from 'rxjs';

import { IBgxHttpInterceptor } from './http-interceptor.interface';
import { IBgxHttpInterceptorMatcher } from './http-interceptor-matcher.interface';
import { IBgxHttpInterceptorMapping } from './http-interceptor-mapping.interface';

import { IBgxHttpRESTOptionsWithBody } from '../http.interfaces';

export interface IBgxHttpInterceptorConfig {
  interceptor: Type<any>;
  paths: string[];
}

export class BgxInterceptorBehaviorService {
  private _requestInterceptors: IBgxHttpInterceptorMapping[] = [];

  get requestInterceptors(): IBgxHttpInterceptorMapping[] {
    return this._requestInterceptors;
  }

  get httpInterceptorMatcher(): IBgxHttpInterceptorMatcher {
    return this._httpInterceptorMatcher;
  }

  constructor(
    private _injector: Injector,
    private _httpInterceptorMatcher: IBgxHttpInterceptorMatcher,
    requestInterceptorConfigs: IBgxHttpInterceptorConfig[],
  ) {
    requestInterceptorConfigs.forEach((config: IBgxHttpInterceptorConfig) => {
      this._requestInterceptors.push({
        interceptor: <IBgxHttpInterceptor>_injector.get(config.interceptor),
        paths: config.paths,
      });
    });
  }
}

@Injectable()
export class BgxHttpService extends HttpClient {
  constructor(private _handler: HttpHandler, private _interceptorBehavior: BgxInterceptorBehaviorService) {
    super(_handler);
  }

  request(
    first: string | HttpRequest<any>,
    url?: string,
    options: {
      body?: any;
      headers?: HttpHeaders | { [header: string]: string | string[] };
      observe?: 'body' | 'events' | 'response';
      params?: HttpParams | { [param: string]: string | string[] };
      reportProgress?: boolean;
      responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
      withCredentials?: boolean;
    } = {},
  ): Observable<any> {
    const interceptors: IBgxHttpInterceptor[] = this._interceptorBehavior.requestInterceptors
      .filter((mapping: IBgxHttpInterceptorMapping) => {
        return this._interceptorBehavior.httpInterceptorMatcher.matches({ url }, mapping);
      })
      .map((mapping: IBgxHttpInterceptorMapping) => {
        return mapping.interceptor;
      });
    return this._setupRequest(first, url, options, interceptors);
  }

  private _setupRequest(
    method: string | HttpRequest<any>,
    url: string,
    options: IBgxHttpRESTOptionsWithBody,
    interceptors: IBgxHttpInterceptor[],
  ): Observable<HttpResponse<any>> {
    try {
      options = this._requestResolve(options, interceptors);
    } catch (e) {
      return new Observable<any>((subscriber: Subscriber<any>) => {
        subscriber.error(e);
      });
    }
    return this._handleResponseResolve(super.request(<any>method, url, options), interceptors);
  }

  private _requestResolve(
    options: IBgxHttpRESTOptionsWithBody,
    interceptors: IBgxHttpInterceptor[],
  ): IBgxHttpRESTOptionsWithBody {
    interceptors.forEach((interceptor: IBgxHttpInterceptor) => {
      if (interceptor.handleOptions) {
        options = interceptor.handleOptions(options);
      }
    });
    return options;
  }

  private _handleResponseResolve(obs: Observable<any>, interceptors: IBgxHttpInterceptor[]): Observable<any> {
    interceptors.reverse();
    interceptors.forEach((interceptor: IBgxHttpInterceptor) => {
      if (interceptor.handleResponse) {
        obs = interceptor.handleResponse(obs);
      }
    });
    return obs;
  }
}
